// ── File I/O: parse and generate CSV / XLSX ──────────────────────────
// Pure JS module — no JSX. Handles browser-side file reading and writing
// for the data transformer. Follows the same convention as `data.js`.

import Papa from 'papaparse';
import * as XLSX from 'xlsx';

// Max upload size — keep in sync with server/app/routers/pipelines.py's
// UPLOAD_MAX_BYTES. Checked client-side (parseFile, below) so an oversized
// file is rejected before the browser spends time parsing it, not just
// after the server reads the whole upload body.
export const MAX_UPLOAD_BYTES = 350 * 1024 * 1024; // 350 MB

export function maxUploadSizeLabel() {
  return formatFileSize(MAX_UPLOAD_BYTES);
}

// ── Parse a File object (from <input> or drag-drop) ──────────────────
// Returns a Promise resolving to { rows, columns, fileName, fileSize }.
export function parseFile(file) {
  if (file.size > MAX_UPLOAD_BYTES) {
    return Promise.reject(new Error(
      `"${file.name}" is ${formatFileSize(file.size)} — the maximum upload size is ${maxUploadSizeLabel()}.`
    ));
  }
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (ext === 'csv' || ext === 'tsv') return parseCSV(file);
  if (['xlsx', 'xlsm', 'xls', 'xlsb'].includes(ext)) return parseXLSX(file);
  return Promise.reject(new Error(`Unsupported format: .${ext}. Use CSV, TSV, XLSX, XLSM, XLS, or XLSB.`));
}

function parseCSV(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      complete: (results) => {
        if (results.errors.length && !results.data.length) {
          reject(new Error(results.errors[0].message));
          return;
        }
        const rows = results.data;
        const columns = inferColumns(rows);
        resolve({ rows, columns, fileName: file.name, fileSize: file.size });
      },
      error: (err) => reject(err),
    });
  });
}

// ── Table boundary detection ─────────────────────────────────────────
// A sheet is not always a clean header-in-row-1 table. The MAS Input sheet
// in the Forex workbook opens with three title/caption lines, puts its real
// header on row 4, and ends with two footnote lines; reading row 1 as the
// header produced a garbage schema and garbage rows. So find the header by
// fill density instead of assuming row 1, take the column span from the
// header plus its data, and trim trailing footnotes.
//
// Mirrors extract_table() in server/app/engine/input_parser.py — the two
// must agree, since the client previews its own parse while the server
// re-parses the stored file for /preview and /input-data.
const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';

function fillCount(row, lo = 0, hi = Infinity) {
  let n = 0;
  for (let c = lo; c < Math.min(row.length, hi); c++) if (!isBlank(row[c])) n++;
  return n;
}

// 0 -> A, 25 -> Z, 26 -> AA (Excel column naming) — synthetic headers and
// Data Preview's letter row. Mirrors col_index_to_letter() in
// server/app/engine/helpers.py.
export function colLetter(index) {
  let name = '';
  let n = index;
  for (;;) {
    name = String.fromCharCode(65 + (n % 26)) + name;
    n = Math.floor(n / 26) - 1;
    if (n < 0) return name;
  }
}

export function extractTable(grid) {
  if (!grid?.length) return [];
  const fills = grid.map(r => fillCount(r));
  const maxFill = Math.max(0, ...fills);
  if (maxFill === 0) return [];

  // The header is the first row wide enough to be one — title/caption lines
  // above it are typically a single cell, so they fall out.
  const threshold = Math.max(1, Math.ceil(maxFill * 0.6));
  const headerIdx = fills.findIndex(f => f >= threshold);
  const header = grid[headerIdx];
  const body = grid.slice(headerIdx + 1).filter(r => fillCount(r) > 0);

  // Span covers header AND data, so a column blank in the header but filled
  // in the data (MAS Input's month/day) is still captured.
  let loCol = null;
  let hiCol = 0;
  for (const row of [header, ...body]) {
    for (let c = 0; c < row.length; c++) {
      if (isBlank(row[c])) continue;
      loCol = loCol === null ? c : Math.min(loCol, c);
      hiCol = Math.max(hiCol, c);
    }
  }
  if (loCol === null) return [];
  const nCols = hiCol - loCol + 1;

  // Footnotes below the table are one or two stray cells wide. Only TRAILING
  // rows are trimmed, so a genuinely sparse row mid-table survives.
  const minFill = nCols >= 4 ? Math.max(1, Math.ceil(nCols * 0.25)) : 1;
  while (body.length && fillCount(body[body.length - 1], loCol, hiCol + 1) < minFill) body.pop();

  const headers = [];
  const seen = new Map();
  for (let c = loCol; c <= hiCol; c++) {
    const raw = header[c];
    let name = isBlank(raw) ? `column_${colLetter(c)}` : String(raw).trim();
    if (seen.has(name)) {
      const n = seen.get(name) + 1;
      seen.set(name, n);
      name = `${name}_${n}`;
    } else {
      seen.set(name, 1);
    }
    headers.push(name);
  }

  return body.map((row) => {
    const out = {};
    for (let k = 0; k < headers.length; k++) {
      const v = row[loCol + k];
      out[headers[k]] = v === null || v === undefined ? '' : v;
    }
    return out;
  });
}

function parseXLSX(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
        // Parse every sheet up front — the workbook is already fully loaded
        // in memory at this point, so this costs nothing extra to defer.
        // `allSheets` keys are the sheet's own name (e.g. "OANDA Input");
        // callers that only care about single-sheet files can ignore it and
        // use the flattened rows/columns below, which are always sheet 1's.
        const allSheets = {};
        for (const name of wb.SheetNames) {
          // header:1 gives the raw grid; extractTable finds the real table in it.
          const grid = XLSX.utils.sheet_to_json(wb.Sheets[name], {
            header: 1, defval: null, blankrows: true,
          });
          const sheetRows = extractTable(grid);
          allSheets[name] = { rows: sheetRows, columns: inferColumns(sheetRows) };
        }
        const sheetName = wb.SheetNames[0];
        const { rows, columns } = allSheets[sheetName];
        resolve({
          rows, columns, fileName: file.name, fileSize: file.size,
          sheetNames: wb.SheetNames, allSheets,
        });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

// App.jsx tags every row it holds with `__rowIndex`, plus `id: row_<n>`
// when the row had no id of its own (onPickInputFile, the sheets-upsert
// loop) — bookkeeping for row identity/diffing, never real data. Server-
// computed step output never carries these, so this only ever matches
// client-tagged rows. Mirrors the same synthetic-id heuristic in
// server/app/engine/output.py's `_clean_rows`: `id` is dropped only when
// EVERY sampled row's value looks synthetic — a real id column stays.
const SYNTHETIC_ID = /^row_\d+$/;

// A column counts as a date when most of its sampled values look like one
// of these — covers Cast Type's/Parse Date's own output formats (ISO
// "YYYY-MM-DD", the ISO-with-time Cast Type also accepts on input, and the
// US/EU slash formats Parse Date can write) plus the common dotted/compact
// variants _try_parse_date (parsing.py) recognizes on input. This is a
// display-only heuristic for Data Preview's type badge/formatting — it
// doesn't feed anything the engine itself relies on.
const DATE_PATTERNS = [
  /^\d{4}-\d{2}-\d{2}$/,                  // 2026-05-23
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/,  // 2026-05-23T00:00:00[...]
  /^\d{1,2}\/\d{1,2}\/\d{4}$/,             // 5/23/2026 or 23/5/2026 (US/EU)
  /^\d{4}\/\d{1,2}\/\d{1,2}$/,             // 2026/5/23
  /^\d{1,2}\.\d{1,2}\.\d{4}$/,             // 23.05.2026
  /^\d{4}\.\d{1,2}\.\d{1,2}$/,             // 2026.05.23
];
const looksLikeDate = (s) => DATE_PATTERNS.some(re => re.test(s));

// ── Infer column types from the first N rows ─────────────────────────
// Returns the same { name, type } shape as SOURCE_COLUMNS.
// Exported so the editor can rebuild the preview/inspector schema from
// live step output (needed when a step ADDS columns, e.g. scaling, or
// changes a column's values in place, e.g. Cast Type/Parse Date).
export function inferColumns(rows, sampleSize = 100) {
  if (!rows.length) return [];
  const sample = rows.slice(0, sampleSize);
  const dropId = sample.every(r => 'id' in r) && sample.every(r => SYNTHETIC_ID.test(r.id));
  // Union of keys across the whole sample, not just row 0 — a column a
  // block only adds conditionally per row (e.g. Split Column skipping a
  // row whose "Apply only when" gate is false) must still show up even
  // when the very first sampled row doesn't have it. Order is first-seen:
  // row 0's keys still lead, anything introduced by a later row is
  // appended after, in the order it's first encountered.
  const seen = new Set();
  const keys = [];
  for (const row of sample) {
    for (const k of Object.keys(row)) {
      if (seen.has(k)) continue;
      seen.add(k);
      if (k === '__rowIndex' || (k === 'id' && dropId)) continue;
      keys.push(k);
    }
  }
  return keys.map(name => {
    let numCount = 0, dateCount = 0, total = 0;
    for (const row of sample) {
      const v = row[name];
      if (v == null || String(v).trim() === '') continue;
      total++;
      const raw = String(v).trim();
      const s = raw.replace(/[$,\s%€£¥]/g, '');
      if (s !== '' && !isNaN(Number(s))) numCount++;
      if (looksLikeDate(raw)) dateCount++;
    }
    const type = total > 0 && numCount / total > 0.7 ? 'number'
      : total > 0 && dateCount / total > 0.7 ? 'date'
        : 'string';
    return { name, type };
  });
}

// ── Format file size for display ─────────────────────────────────────
export function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// ── Output directory handle (File System Access API) ─────────────────
// Set when the user picks a local folder via showDirectoryPicker() in the
// Run modal's folder browser (Preview.jsx) — kept only so that click still
// records a handle; nothing currently reads it back (the write-to-folder
// path this fed, `downloadData`, was dead code with zero callers and was
// removed — see plan.md). Run pipeline's actual delivery is a browser
// download of the server-written file (App.jsx's `onRun`).
let _outputDirHandle = null;

export function setOutputDirHandle(handle) { _outputDirHandle = handle; }
